import {
  Component,
  ChangeDetectionStrategy,
  input,
  inject,
  DestroyRef,
  signal,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, of } from 'rxjs';
import { faCalendar, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';

import { StudentProfile } from '../../../api/models/student-profile';
import { StudentProfileService } from '../../../api/services/student-profile.service';
import { ModalService } from '../../../shared/services/modal.service';
import { AlertService, alertMessages } from '../../../shared/services/alert.service';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { ProfileTabCardComponent } from './profile-tab-card.component';

@Component({
  selector: 'app-student-experienceday-tab',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslateModule, FontAwesomeModule, ProfileTabCardComponent],
  template: `
    <app-profile-tab-card
      title="Follow-up day registrations"
      [icon]="faCalendar"
      iconColorClass="text-purple"
      [count]="plannedExperiencedays().data.length"
    >
      @if (plannedExperiencedays().data.length > 0) {
        <div class="overflow-x-auto">
          <table class="w-full text-sm text-left text-mty-body">
            <thead class="text-xs uppercase tracking-wide text-mty-body/60">
              <tr>
                <th class="py-2 pr-3 font-medium">{{ 'Follow-up day' | translate }}</th>
                <th class="py-2 pr-3 font-medium">{{ 'Company' | translate }}</th>
                <th class="py-2 pr-3 font-medium">{{ 'Date' | translate }}</th>
                <th class="py-2 pr-3 sr-only">{{ 'Actions' | translate }}</th>
              </tr>
            </thead>
            <tbody>
              @for (node of plannedExperiencedays().data; track node['id']) {
                <tr class="border-t border-gray-100 odd:bg-gray-50/60">
                  <td class="py-2 pr-3">{{ node['experienceDay']['title'] }}</td>
                  <td class="py-2 pr-3">{{ node['company']['name'] }}</td>
                  <td class="py-2 pr-3 whitespace-nowrap">
                    {{ node['date'] | date: 'd-MM-YYYY' }}
                  </td>
                  <td class="py-2 pr-3 text-right">
                    <button
                      type="button"
                      class="inline-flex items-center gap-1.5 text-sm font-medium text-mty-danger hover:text-mty-body transition-colors"
                      (click)="deletePlannedExperienceday(node)"
                    >
                      <fa-icon [icon]="faTimes" class="text-[10px]"></fa-icon>
                      <span>{{ 'Unsubscribe' | translate }}</span>
                    </button>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      } @else {
        <div class="text-center py-8 px-4 text-sm text-mty-body/60">
          <p>{{ 'No results' | translate }}</p>
        </div>
      }
    </app-profile-tab-card>
  `,
})
export class StudentExperiencedayTabComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly modal = inject(ModalService);
  private readonly alertService = inject(AlertService);
  private readonly studentProfileService = inject(StudentProfileService);

  entity = input.required<StudentProfile>();

  protected readonly faCalendar = faCalendar;
  protected readonly faTimes = faTimes;

  readonly plannedExperiencedays = signal({
    pageSize: 100,
    pageNumber: 1,
    totalItems: 0,
    data: [] as any[],
  });

  ngOnInit() {
    this.loadPlannedExperiencedays();
  }

  private loadPlannedExperiencedays(): void {
    if (!this.entity().id) return;

    this.studentProfileService
      .getStudentPlannedExperienceDays({ profileId: this.entity().id! })
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((response: any) => {
        if (response) {
          this.plannedExperiencedays.update((v) => ({
            ...v,
            data: [...response.data],
            totalItems: response.totalItems,
          }));
        }
      });
  }

  deletePlannedExperienceday(plannedexperienceday: any) {
    if (!plannedexperienceday.hasOwnProperty('studentProfileId')) return;
    if (!plannedexperienceday.hasOwnProperty('id')) return;

    this.modal
      .Confirm('Unsubscribe', 'Are you sure you want to unsubscribe?')
      .then((confirm) => {
        if (confirm) {
          this.studentProfileService
            .deleteStudentPlannedExperienceDay({
              profileId: plannedexperienceday['studentProfileId'],
              id: plannedexperienceday['id'],
            })
            .pipe(
              catchError(() => {
                this.modal.Alert('Unsubscribe', marker('It is not possible anymore to unsubscribe for this event'));
                return of(null);
              }),
              takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(() => {
              this.alertService.success(alertMessages.successDataRemoved);
              this.loadPlannedExperiencedays();
            });
        }
      })
      .catch(() => {});
  }
}
